'use client';

import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { createClient, clearAuthStorage } from '@/lib/supabase/client';
import type { User, Session } from '@supabase/supabase-js';
import { invalidateUserCache } from '@/lib/services/tradeService';

interface UserProfile {
  id: string;
  auth_user_id: string | null;
  full_name: string;
  email: string;
  user_type: string;
  status: string;
  timezone: string;
  default_currency: string;
}

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, fullName?: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  session: null,
  profile: null,
  loading: true,
  signIn: async () => {},
  signUp: async () => {},
  signOut: async () => {},
  refreshProfile: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const supabase = createClient();
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const initDone = useRef(false);

  const fetchProfile = useCallback(
    async (authUser: User) => {
      try {
        const { data } = await supabase
          .from('managed_users')
          .select('id, auth_user_id, full_name, email, status')
          .eq('auth_user_id', authUser.id)
          .maybeSingle();

        if (data) {
          setProfile({
            id: data.id,
            auth_user_id: data.auth_user_id,
            full_name: data.full_name,
            email: data.email,
            user_type: 'user',
            status: data.status,
            timezone: 'UTC',
            default_currency: 'USD',
          });
        } else {
          setProfile({
            id: authUser.id,
            auth_user_id: authUser.id,
            full_name: authUser.user_metadata?.full_name || authUser.email?.split('@')[0] || 'User',
            email: authUser.email || '',
            user_type: 'user',
            status: 'active',
            timezone: 'UTC',
            default_currency: 'USD',
          });
        }
      } catch {
        setProfile(null);
      }
    },
    [supabase]
  );

  const refreshProfile = useCallback(async () => {
    if (user) await fetchProfile(user);
  }, [user, fetchProfile]);

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      try {
        const {
          data: { user: authUser },
        } = await supabase.auth.getUser();

        if (!mounted) return;

        if (authUser) {
          const {
            data: { session: s },
          } = await supabase.auth.getSession();
          setSession(s);
          setUser(authUser);
          await fetchProfile(authUser);
        } else {
          setSession(null);
          setUser(null);
          setProfile(null);
        }
      } catch {
        if (mounted) {
          setSession(null);
          setUser(null);
          setProfile(null);
        }
      } finally {
        if (mounted) {
          initDone.current = true;
          setLoading(false);
        }
      }
    };

    init();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, s) => {
      if (!mounted || !initDone.current) return;
      if (event === 'INITIAL_SESSION') return;

      setSession(s);
      setUser(s?.user ?? null);

      if (s?.user) {
        await fetchProfile(s.user);
      } else {
        setProfile(null);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [supabase, fetchProfile]);

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;

    if (data.user) {
      const { data: managedUser } = await supabase
        .from('managed_users')
        .select('status')
        .eq('auth_user_id', data.user.id)
        .maybeSingle();

      if (managedUser && (managedUser.status === 'suspended' || managedUser.status === 'inactive')) {
        await supabase.auth.signOut();
        const label = managedUser.status === 'suspended' ? 'suspended' : 'inactive';
        throw new Error(
          `Your account has been ${label}. Please contact an administrator.`
        );
      }
    }
  };

  const signUp = async (email: string, password: string, fullName?: string) => {
    const normalizedEmail = email.trim().toLowerCase();
    const name = fullName?.trim() || email.split('@')[0];

    const { data, error } = await supabase.auth.signUp({
      email: normalizedEmail,
      password,
      options: { data: { full_name: name } },
    });
    if (error) throw error;

    // If email confirmation is required there is no session yet — try to sign in.
    if (!data.session) {
      const { error: signInErr } = await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password,
      });
      if (signInErr) {
        throw new Error(
          'Account created. Please verify your email, then sign in.'
        );
      }
    }

    const authUserId = data.user?.id;
    if (!authUserId) return;

    // Provision the managed_users profile + default role. This is best-effort:
    // the user is already signed in, so a failure here must NOT block sign-up.
    await provisionManagedUser(authUserId, normalizedEmail, name);
  };

  // Resolve managed_users by email/auth_user_id to avoid UNIQUE conflicts
  // (both email and auth_user_id are unique), then ensure a default role.
  const provisionManagedUser = async (
    authUserId: string,
    normalizedEmail: string,
    name: string
  ) => {
    try {
      // Look up by email first, then by auth_user_id (both are UNIQUE)
      let existing: { id: string; auth_user_id: string | null } | null = null;

      const byEmail = await supabase
        .from('managed_users')
        .select('id, auth_user_id')
        .eq('email', normalizedEmail)
        .maybeSingle();
      existing = byEmail.data ?? null;

      if (!existing) {
        const byAuth = await supabase
          .from('managed_users')
          .select('id, auth_user_id')
          .eq('auth_user_id', authUserId)
          .maybeSingle();
        existing = byAuth.data ?? null;
      }

      let managedUserId = existing?.id ?? null;

      if (existing) {
        // Re-link the existing profile to the current auth user if needed
        if (existing.auth_user_id !== authUserId) {
          await supabase
            .from('managed_users')
            .update({ auth_user_id: authUserId, full_name: name, status: 'active' })
            .eq('id', existing.id);
        }
      } else {
        const { data: inserted } = await supabase
          .from('managed_users')
          .insert({
            auth_user_id: authUserId,
            full_name: name,
            email: normalizedEmail,
            status: 'active',
          })
          .select('id')
          .maybeSingle();
        managedUserId = inserted?.id ?? null;
      }

      if (!managedUserId) return;

      const { data: employeeRole } = await supabase
        .from('rbac_roles')
        .select('id')
        .eq('name', 'Employee')
        .maybeSingle();

      if (employeeRole?.id) {
        // Skip if the role is already assigned (composite PK)
        const { data: hasRole } = await supabase
          .from('user_roles')
          .select('role_id')
          .eq('user_id', managedUserId)
          .eq('role_id', employeeRole.id)
          .maybeSingle();

        if (!hasRole) {
          await supabase
            .from('user_roles')
            .insert({ user_id: managedUserId, role_id: employeeRole.id });
        }
      }
    } catch {
      // Best-effort: ignore — sign-in already succeeded
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch {
      // Ignore network/session errors — we still clear local state below
    }
    invalidateUserCache();
    clearAuthStorage();
    setUser(null);
    setSession(null);
    setProfile(null);
    // Hard redirect to login so all in-memory state is dropped
    window.location.href = '/sign-up-login';
  };

  return (
    <AuthContext.Provider
      value={{ user, session, profile, loading, signIn, signUp, signOut, refreshProfile }}
    >
      {children}
    </AuthContext.Provider>
  );
};
